import { AxiosResponse } from "axios";
import { MessageEventApi } from "go-component/components/MessageEvent/services/types";
import { parseApiData } from "go-core/api/parseApiData";
import { parseApiListDataResponse } from "go-core/api/parseApiListDataResponse";
import { getSearchSelectPage, mapToBase } from "go-core/api/searchSelectService";
import { ListDataRequestResponse, MoneyApi } from "go-core/api/types";
import { restSecurityApi } from "go-security/services/Api/api";
import OrganizationIdService from "go-security/services/organizationIdService";
import { CustomFieldApi, CustomFieldTemplateApi } from "go-segment/services/types";
import {
	OrderSummaryApi,
	PurchaseSummaryApi,
	StockAdjustmentSummaryApi,
	TransferSummaryApi,
} from "../../../pages/App/pages/Organization/pages/Document/services/types";
import { GusApi, SearchSelectApi } from "../types";
import {
	AlertsApi,
	AppLinkApi,
	ApplicationApi,
	ApplicationTemplateApi,
	ApplicationUpdateApi,
	ArchivesApi,
	AutofillStocktakingDraftApi,
	BundleApi,
	CompanyApi,
	ContactApi,
	CorrectionFullApi,
	CreateAndCloseLinkedApi,
	CustomFieldSearchSelectApi,
	DiagnosticProductApi,
	DiagnosticProductTransactionResponse,
	InfoDiagnostic2ReportApi,
	InvoiceApi,
	InvoiceDetailsApi,
	InvoicePaymentApi,
	JpkExportBodyApi,
	LinkEntityFormProps,
	LinkedApi,
	OrderApi,
	OrderSearchSelectApi,
	OrderShipApi,
	PointOfSaleApi,
	ProductApi,
	ProductCategoryApi,
	ProductConnectionSearchSelectApi,
	ProductContactApi,
	ProductGroupsApi,
	ProductItemApi,
	ProductPurchaseApi,
	ProductSalesApi,
	ProductStateApi,
	ProductStockAdjustmentApi,
	ProductTransferApi,
	PurchaseApi,
	PurchaseCorrectionApi,
	PurchaseReceiveApi,
	StockAdjustmentApi,
	StockApi,
	StockStateApi,
	StocktakingApi,
	StocktakingDraftApi,
	StocktakingDraftStateApi,
	StocktakingDraftSummaryStateApi,
	StocktakingProductStateApi,
	StocktakingProductStateSummaryApi,
	StocktakingSummaryApi,
	TaxApi,
	TransactionApi,
	TransferApi,
	WarehouseSettingsApi,
} from "./types";

class apiOrganization {
	static getOrganizationId = () => {
		return OrganizationIdService.getWithCheck();
	};

	static async getVenueMe(id: number) {
		// return restSecurityApi.get(`/security/${id}/me`)
		return restSecurityApi.get(`/${id}/me`);
	}

	// CONTACT
	static async getContacts(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<ContactApi>> {
		return parseApiListDataResponse<ContactApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/contacts`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getContact(contactId: number, params?: Record<string, any>): Promise<ContactApi> {
		return parseApiData(restSecurityApi.get(`/${this.getOrganizationId()}/contacts/${contactId}`, { params }));
	}

	static async createContact(data: ContactApi, params?: Record<string, any>): Promise<ContactApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/contacts`, data, { params }));
	}

	static async updateContact(data: ContactApi, params?: Record<string, any>): Promise<ContactApi> {
		return parseApiData(restSecurityApi.put(`/${this.getOrganizationId()}/contacts/${data.id}`, data, { params }));
	}

	static async restoreContact(contactId: number): Promise<{}> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/contacts/${contactId}/restore`));
	}

	static async removeContact(contactId: number): Promise<{}> {
		return parseApiData(restSecurityApi.delete(`/${this.getOrganizationId()}/contacts/${contactId}`));
	}

	static async getDataByTaxIdNo(tax_id_no: string): Promise<GusApi> {
		return parseApiData(
			restSecurityApi.get(`/${this.getOrganizationId()}/contacts/update_by_tax_id_no?tax_id_no=${tax_id_no}`)
		);
	}

	static async getContactSearchSelect(
		t: string,
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<SearchSelectApi[]> {
		const newObj = JSON.parse(JSON.stringify(params));
		delete newObj.ids;
		delete params.ids;

		const f = mapToBase(t, newObj);
		const page = getSearchSelectPage(newObj);
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/contacts/search_select?f=${f}&page=${page}`, {
				cancelToken: options?.cancelToken,
				params,
			})
		);
	}

	static async linkContact(data: LinkEntityFormProps, params?: Record<string, any>): Promise<ContactApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/contacts/link`, data, { params }));
	}

	static async skipContact(data: LinkEntityFormProps, params?: Record<string, any>): Promise<undefined> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/contacts/skip`, data, { params }));
	}

	static async createLinkedContact(data: CreateAndCloseLinkedApi, params?: Record<string, any>): Promise<ContactApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/contacts/create`, data, { params }));
	}

	// PRODUCT CATEGORY
	static async getProductCategories(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<ProductCategoryApi>> {
		return parseApiListDataResponse<ProductCategoryApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/product_categories`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getProductCategorySearchSelect(
		t: string,
		obj: Record<string, any>,
		options?: Record<string, any>
	): Promise<SearchSelectApi[]> {
		const newObj = JSON.parse(JSON.stringify(obj));
		delete newObj.ids;

		const f = mapToBase(t, newObj);
		const page = getSearchSelectPage(newObj);
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/product_categories/search_select?f=${f}&page=${page}`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getProductCategory(
		productCategoryId: number,
		params?: Record<string, any>
	): Promise<ProductCategoryApi> {
		return parseApiData(
			restSecurityApi.get(`/${this.getOrganizationId()}/product_categories/${productCategoryId}`, { params })
		);
	}

	static async createProductCategory(data: ProductCategoryApi): Promise<ProductCategoryApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/product_categories`, data));
	}

	static async updateProductCategory(
		data: ProductCategoryApi,
		params?: Record<string, any>
	): Promise<ProductCategoryApi> {
		return parseApiData(
			restSecurityApi.put(`/${this.getOrganizationId()}/product_categories/${data.id}`, data, { params })
		);
	}

	static async removeProductCategory(productCategoryId: number): Promise<{}> {
		return parseApiData(
			restSecurityApi.delete(`/${this.getOrganizationId()}/product_categories/${productCategoryId}`)
		);
	}

	static async restoreProductCategory(productCategoryId: number): Promise<{}> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/product_categories/${productCategoryId}/restore`)
		);
	}

	static async removeMultipleCategories(
		categoryIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<ProductCategoryApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/product_categories`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${categoryIds}&${urlParams}`;

		return parseApiData(restSecurityApi.delete(endpoint));
	}

	static async restoreMultipleCategories(
		categoryIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<ProductCategoryApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/product_categories/restore`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${categoryIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async getProductsCategoriesCount(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<number> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/product_categories/count`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	// PRODUCT

	static async getProductSearchSelect(
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<ProductApi[]> {
		const newParams = JSON.parse(JSON.stringify(params));
		delete newParams.ids;
		const f = mapToBase(search, newParams);
		const page = getSearchSelectPage(newParams);
		const include = newParams?.include || "";
		const requestParams: Record<string, any> = {
			include,
		};
		const res = await parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/products/search_select?f=${f}&page=${page}`, {
				params: requestParams,
				cancelToken: options?.cancelToken,
			})
		);

		return res.map((product: Record<string, any>) => ({
			...product,
			name: product.label,
			type: product.product_type,
		}));
	}

	static async getProductState(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ProductStateApi[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/products/states`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getExternalProductState(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ProductStateApi[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/products/external_states`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getProductLastPrice(
		productId: number,
		volume: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<MoneyApi> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/products/${productId}/last_price?volume=${volume}`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getProductItems(
		productId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ProductItemApi[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/products/${productId}/items`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getProductPurchases(
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<ProductPurchaseApi>> {
		return parseApiListDataResponse<ProductPurchaseApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/purchase_orders/items`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getProductSales(
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<ProductSalesApi>> {
		return parseApiListDataResponse<ProductSalesApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/sales_orders/items`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getProductTransfers(
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<ProductTransferApi>> {
		return parseApiListDataResponse<ProductTransferApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/transfer_orders/items`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getProductStockAdjustments(
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<ProductStockAdjustmentApi>> {
		return parseApiListDataResponse<ProductStockAdjustmentApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/stock_adjustments/items`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getProducts(
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<ProductApi>> {
		return parseApiListDataResponse<ProductApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/products`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getProduct(productId: number, params?: Record<string, any>): Promise<ProductApi> {
		return parseApiData(restSecurityApi.get(`${this.getOrganizationId()}/products/${productId}`, { params }));
	}

	static async restoreProduct(productId: number, params?: Record<string, any>): Promise<ProductApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/products/${productId}/restore`, undefined, { params })
		);
	}

	static async restoreMultipleProducts(
		productIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<ProductApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/products/restore`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${productIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async removeProduct(productId: number, params?: Record<string, any>): Promise<ProductApi> {
		return parseApiData(restSecurityApi.delete(`/${this.getOrganizationId()}/products/${productId}`, { params }));
	}

	static async removeMultipleProducts(
		productIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<ProductApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/products`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${productIds}&${urlParams}`;

		return parseApiData(restSecurityApi.delete(endpoint));
	}

	static async getProductsCount(params?: Record<string, any>, options?: Record<string, any>): Promise<number> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/products/count`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async closeLinkedProduct(productId: number, params?: Record<string, any>): Promise<ProductApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/products/${productId}/close_linked_products`, { params })
		);
	}

	static async closeMultipleLinkedProduct(
		productIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<ProductApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/products/close_linked_products`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${productIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async correctProductRecipe(productId: number): Promise<{}> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/products/${productId}/correct_components`)
		);
	}

	static async createProduct(data: ProductApi): Promise<ProductApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/products`, data));
	}

	static async updateProduct(data: ProductApi, params?: Record<string, any>): Promise<ProductApi> {
		return parseApiData(restSecurityApi.put(`/${this.getOrganizationId()}/products/${data.id}`, data, { params }));
	}

	static async mergeProduct(
		productId: number,
		intoProductId: number,
		params?: Record<string, any>
	): Promise<ProductApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/products/${productId}/merge?into=${intoProductId}`, {
				params,
			})
		);
	}

	static async linkProduct(data: LinkEntityFormProps, params?: Record<string, any>): Promise<ProductApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/products/link`, data, { params }));
	}

	static async skipProduct(data: LinkEntityFormProps, params?: Record<string, any>): Promise<undefined> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/products/skip`, data, { params }));
	}

	static async createLinkedProduct(data: CreateAndCloseLinkedApi, params?: Record<string, any>): Promise<ProductApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/products/create`, data, { params }));
	}

	static patchProducts(data: Record<string, any>, params?: Record<string, any>): Promise<ProductApi[]> {
		return parseApiData(restSecurityApi.patch(`${this.getOrganizationId()}/products`, data, { params }));
	}

	// PACKAGE

	static async getProductPackageSearchSelect(
		t: string,
		obj: Record<string, any>,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ProductConnectionSearchSelectApi[]> {
		const newObj = JSON.parse(JSON.stringify(obj));
		delete newObj.ids;

		const f = mapToBase(t, newObj);
		const page = getSearchSelectPage(newObj);

		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/product_packages/search_select?f=${f}&page=${page}`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	// PURCHASE

	static async createPurchase(data: PurchaseApi): Promise<PurchaseApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/purchase_orders`, data));
	}

	static async updatePurchase(data: PurchaseApi, params?: Record<string, any>): Promise<PurchaseApi> {
		return parseApiData(
			restSecurityApi.put(`/${this.getOrganizationId()}/purchase_orders/${data.id}`, data, { params })
		);
	}

	static async getPurchase(purchaseId: number | string, params?: Record<string, any>): Promise<PurchaseApi> {
		return parseApiData(
			restSecurityApi.get(`/${this.getOrganizationId()}/purchase_orders/${purchaseId}`, { params })
		);
	}

	static async confirmPurchase(purchaseId: number | string, params?: Record<string, any>): Promise<PurchaseApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/purchase_orders/${purchaseId}/confirm`, undefined, {
				params,
			})
		);
	}

	static async removePurchase(purchaseId: number | string, params?: Record<string, any>): Promise<PurchaseApi> {
		return parseApiData(
			restSecurityApi.delete(`/${this.getOrganizationId()}/purchase_orders/${purchaseId}`, { params })
		);
	}

	static async blockPurchase(purchaseId: number | string, params?: Record<string, any>): Promise<PurchaseApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/purchase_orders/${purchaseId}/lock`, undefined, {
				params,
			})
		);
	}

	static async unlockPurchase(purchaseId: number | string, params?: Record<string, any>): Promise<PurchaseApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/purchase_orders/${purchaseId}/unlock`, undefined, {
				params,
			})
		);
	}

	static async exportPurchaseEInvoice(purchaseId: number | string): Promise<AxiosResponse> {
		return restSecurityApi.get(`/${this.getOrganizationId()}/purchase_orders/${purchaseId}/efaktura`);
	}

	static async collectPurchase(purchaseId: number | string, params?: Record<string, any>): Promise<PurchaseApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/purchase_orders/${purchaseId}/receive`, undefined, {
				params,
			})
		);
	}

	static async cancelPurchase(purchaseId: number | string, params?: Record<string, any>): Promise<PurchaseApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/purchase_orders/${purchaseId}/cancel`, undefined, {
				params,
			})
		);
	}

	static async restorePurchase(purchaseId: number | string, params?: Record<string, any>): Promise<PurchaseApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/purchase_orders/${purchaseId}/restore`, undefined, {
				params,
			})
		);
	}

	static async getPurchases(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<PurchaseApi>> {
		return parseApiListDataResponse<PurchaseApi, PurchaseSummaryApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/purchase_orders`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getPurchaseSearchSelect(
		string: string,
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<SearchSelectApi[]> {
		const newParams = JSON.parse(JSON.stringify(params));
		delete newParams.ids;

		const f = mapToBase(string, newParams);
		const page = getSearchSelectPage(newParams);
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/purchase_orders/search_select?f=${f}&page=${page}`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getPurchasesCount(params?: Record<string, any>, options?: Record<string, any>): Promise<number> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/purchase_orders/count`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async removePurchases(
		purchaseIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<PurchaseApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/purchase_orders`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${purchaseIds}&${urlParams}`;

		return parseApiData(restSecurityApi.delete(endpoint));
	}

	static async restorePurchases(
		purchaseIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<PurchaseApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/purchase_orders/restore`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${purchaseIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async lockPurchases(
		purchaseIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<PurchaseApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/purchase_orders/lock`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${purchaseIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async unlockPurchases(
		purchaseIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<PurchaseApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/purchase_orders/unlock`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${purchaseIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async confirmPurchases(
		purchaseIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<PurchaseApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/purchase_orders/confirm`;
		const endpoint = areAllItemsSelected ? `${baseEndpoint}?${urlParams}` : `${baseEndpoint}?ids=${purchaseIds}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async cancelPurchases(
		purchaseIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<PurchaseApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/purchase_orders/cancel`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${purchaseIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async receivePurchases(
		purchaseIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<PurchaseApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/purchase_orders/receive`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${purchaseIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async deletePurchasesReceive(
		purchaseIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<PurchaseApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/purchase_orders/receives`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?purchase_order_ids=${purchaseIds}&${urlParams}`;

		return parseApiData(restSecurityApi.delete(endpoint));
	}

	static async cancelPurchaseReceive(
		purchaseId: number | string,
		params?: Record<string, any>
	): Promise<PurchaseApi> {
		return parseApiData(
			restSecurityApi.post(
				`/${this.getOrganizationId()}/purchase_orders/${purchaseId}/cancel_receive`,
				undefined,
				{
					params,
				}
			)
		);
	}

	static async savePurchaseFromFile(file: FormData): Promise<PurchaseApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/purchase_orders/from_file`, file));
	}

	static async previewPurchaseFromFile(file: FormData): Promise<PurchaseApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/purchase_orders/preview_from_file`, file)
		);
	}

	// PURCHASE RECEIVE

	static async getPurchaseReceives(
		purchaseId: number | string,
		params?: Record<string, any>
	): Promise<PurchaseReceiveApi[]> {
		return parseApiData(
			restSecurityApi.get(`/${this.getOrganizationId()}/purchase_orders/${purchaseId}/receives`, { params })
		);
	}

	static async removePurchaseReceives(purchaseId: number | string, receiveId: number): Promise<PurchaseReceiveApi[]> {
		return parseApiData(
			restSecurityApi.delete(`/${this.getOrganizationId()}/purchase_orders/${purchaseId}/receives/${receiveId}`)
		);
	}

	// PURCHASE CORRECTION

	static async getPurchaseCorrections(
		purchaseId: number | string,
		params?: Record<string, any>
	): Promise<PurchaseCorrectionApi[]> {
		return parseApiData(
			restSecurityApi.get(`/${this.getOrganizationId()}/purchase_orders/${purchaseId}/corrections`, { params })
		);
	}

	static async getPurchaseCorrection(
		purchaseId: number | string,
		correctionId: number,
		params?: Record<string, any>
	): Promise<PurchaseCorrectionApi> {
		return parseApiData(
			restSecurityApi.get(
				`/${this.getOrganizationId()}/purchase_orders/${purchaseId}/corrections/${correctionId}`,
				{ params }
			)
		);
	}

	static async updatePurchaseCorrection(
		purchaseId: number | string,
		correctionId: number,
		data: PurchaseCorrectionApi,
		params?: Record<string, any>
	): Promise<PurchaseCorrectionApi> {
		return parseApiData(
			restSecurityApi.put(
				`/${this.getOrganizationId()}/purchase_orders/${purchaseId}/corrections/${correctionId}`,
				data,
				{ params }
			)
		);
	}

	static async createPurchaseCorrection(
		purchaseId: number | string,
		data: PurchaseCorrectionApi,
		params?: Record<string, any>
	): Promise<PurchaseCorrectionApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/purchase_orders/${purchaseId}/corrections`, data, {
				params,
			})
		);
	}

	static async removePurchaseCorrection(correctionId: number): Promise<{}> {
		return parseApiData(restSecurityApi.delete(`/${this.getOrganizationId()}/corrections/${correctionId}`));
	}

	static async closePurchaseCorrection(
		correctionId: number,
		params?: Record<string, any>
	): Promise<PurchaseCorrectionApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/corrections/${correctionId}/close`, undefined, {
				params,
			})
		);
	}

	static async closeCancelPurchaseCorrection(
		correctionId: number,
		params?: Record<string, any>
	): Promise<PurchaseCorrectionApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/corrections/${correctionId}/close_cancel`, undefined, {
				params,
			})
		);
	}

	// STOCK

	static async getStockSearchSelect(
		t: string,
		obj: Record<string, any>,
		options?: Record<string, any>
	): Promise<SearchSelectApi[]> {
		const newObj = JSON.parse(JSON.stringify(obj));
		delete newObj.ids;

		const f = mapToBase(t, newObj);
		const page = getSearchSelectPage(newObj);
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/stocks/search_select?f=${f}&page=${page}`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getStocks(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<StockApi>> {
		return parseApiListDataResponse<StockApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/stocks`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getStock(id: string, params?: Record<string, any>): Promise<StockApi> {
		return parseApiData(restSecurityApi.get(`${this.getOrganizationId()}/stocks/${id}`, { params }));
	}

	static async updateStock(data: StockApi, params?: Record<string, any>): Promise<StockApi> {
		return parseApiData(restSecurityApi.put(`${this.getOrganizationId()}/stocks/${data.id}`, data, { params }));
	}

	static async createStock(data: StockApi, params?: Record<string, any>): Promise<StockApi> {
		return parseApiData(restSecurityApi.post(`${this.getOrganizationId()}/stocks`, data, { params }));
	}

	static async removeStock(stockId: number): Promise<{}> {
		return parseApiData(restSecurityApi.delete(`/${this.getOrganizationId()}/stocks/${stockId}`));
	}

	static async correctStates(params?: Record<string, any>): Promise<{}> {
		return parseApiData(
			restSecurityApi.post(`${this.getOrganizationId()}/correct_incoming_committed`, undefined, { params })
		);
	}

	// ORDER
	static async getOrders(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<OrderApi>> {
		return parseApiListDataResponse<OrderApi, OrderSummaryApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/sales_orders`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getOrderSearchSelect(
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<OrderSearchSelectApi[]> {
		const newObj = JSON.parse(JSON.stringify(params));
		delete newObj.ids;

		const f = mapToBase(search, newObj);
		const page = getSearchSelectPage(newObj);
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/sales_orders/search_select?f=${f}&page=${page}`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getOrder(orderId: number | string, params?: Record<string, any>): Promise<OrderApi> {
		return parseApiData(restSecurityApi.get(`${this.getOrganizationId()}/sales_orders/${orderId}`, { params }));
	}

	static async updateOrder(data: OrderApi, params?: Record<string, any>): Promise<OrderApi> {
		return parseApiData(
			restSecurityApi.put(`${this.getOrganizationId()}/sales_orders/${data.id}`, data, { params })
		);
	}

	static async createOrderShip(
		orderId: number | string,
		data: OrderShipApi,
		params?: Record<string, any>
	): Promise<OrderShipApi> {
		return parseApiData(
			restSecurityApi.post(`${this.getOrganizationId()}/sales_orders/${orderId}/ships`, data, { params })
		);
	}

	static async getOrderShips(
		orderId: string | number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<OrderShipApi[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/sales_orders/${orderId}/ships`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async removeOrderShip(orderId: number | string, orderShipId: number): Promise<OrderShipApi[]> {
		return parseApiData(
			restSecurityApi.delete(`/${this.getOrganizationId()}/sales_orders/${orderId}/ships/${orderShipId}`)
		);
	}

	static async removeOrder(orderId: number | string, params?: Record<string, any>): Promise<OrderApi> {
		return parseApiData(restSecurityApi.delete(`/${this.getOrganizationId()}/sales_orders/${orderId}`, { params }));
	}

	static async restoreOrder(orderId: number | string, params?: Record<string, any>): Promise<OrderApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/sales_orders/${orderId}/restore`, undefined, { params })
		);
	}

	static async cancelOrder(orderId: number | string, params?: Record<string, any>): Promise<OrderApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/sales_orders/${orderId}/cancel`, undefined, { params })
		);
	}

	static async lockOrder(orderId: number | string, params?: Record<string, any>): Promise<OrderApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/sales_orders/${orderId}/lock`, undefined, { params })
		);
	}

	static async unlockOrder(orderId: number | string, params?: Record<string, any>): Promise<OrderApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/sales_orders/${orderId}/unlock`, undefined, { params })
		);
	}

	static async confirmOrder(orderId: number | string, params?: Record<string, any>): Promise<OrderApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/sales_orders/${orderId}/confirm`, undefined, { params })
		);
	}

	static confirmOnDateOrder(
		orderIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<OrderApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/sales_orders/confirm`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${orderIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async createOrder(data: OrderApi): Promise<OrderApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/sales_orders`, data));
	}

	static removeMultipleOrders(
		orderIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<{}> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/sales_orders`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${orderIds}&${urlParams}`;

		return parseApiData(restSecurityApi.delete(endpoint));
	}

	static restoreMultipleOrders(
		orderIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<OrderApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/sales_orders/restore`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${orderIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static lockMultipleOrders(
		orderIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<OrderApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/sales_orders/lock`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${orderIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static unlockMultipleOrders(
		orderIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<OrderApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/sales_orders/unlock`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${orderIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static cancelMultipleOrders(
		orderIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<OrderApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/sales_orders/cancel`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${orderIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static confirmMultipleOrders(
		orderIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<OrderApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/sales_orders/confirm`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${orderIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async exportOrderEInvoice(orderId: number | string): Promise<AxiosResponse> {
		return restSecurityApi.get(`/${this.getOrganizationId()}/sales_orders/${orderId}/efaktura`);
	}

	static async getSalesOrdersCount(params?: Record<string, any>, options?: Record<string, any>): Promise<number> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/sales_orders/count`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async saveOrderFromFile(file: FormData): Promise<OrderApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/sales_orders/from_file`, file));
	}

	static async previewOrderFromFile(file: FormData): Promise<OrderApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/sales_orders/preview_from_file`, file));
	}

	// RESOURCE TYPES
	static async getResourceTypes(
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<{
		custom_fields: CustomFieldTemplateApi[];
	}> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/resource_config`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	//STOCK ADJUSTMENTS
	static async getStockAdjustments(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<StockAdjustmentApi>> {
		return parseApiListDataResponse<StockAdjustmentApi, StockAdjustmentSummaryApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/stock_adjustments`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getStockAdjustment(
		stockAdjustmentId: number,
		params?: Record<string, any>
	): Promise<StockAdjustmentApi> {
		return parseApiData(
			restSecurityApi.get(`/${this.getOrganizationId()}/stock_adjustments/${stockAdjustmentId}`, { params })
		);
	}

	static async updateStockAdjustment(
		data: StockAdjustmentApi,
		params?: Record<string, any>
	): Promise<StockAdjustmentApi> {
		return parseApiData(
			restSecurityApi.put(`/${this.getOrganizationId()}/stock_adjustments/${data.id}`, data, { params })
		);
	}

	static async createStockAdjustment(
		data: StockAdjustmentApi,
		params?: Record<string, any>
	): Promise<StockAdjustmentApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/stock_adjustments`, data, { params }));
	}

	static confirmStockAdjustments(
		stockAdjustmentIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<StockAdjustmentApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/stock_adjustments/confirm`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${stockAdjustmentIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async confirmStockAdjustment(
		stockAdjustmentId: number,
		params?: Record<string, any>
	): Promise<StockAdjustmentApi> {
		return parseApiData(
			restSecurityApi.post(
				`${this.getOrganizationId()}/stock_adjustments/${stockAdjustmentId}/confirm`,
				undefined,
				{ params }
			)
		);
	}

	static cancelStockAdjustments(
		stockAdjustmentIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<StockAdjustmentApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/stock_adjustments/cancel`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${stockAdjustmentIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async cancelStockAdjustment(
		stockAdjustmentId: number,
		params?: Record<string, any>
	): Promise<StockAdjustmentApi> {
		return parseApiData(
			restSecurityApi.post(
				`${this.getOrganizationId()}/stock_adjustments/${stockAdjustmentId}/cancel`,
				undefined,
				{ params }
			)
		);
	}

	static closeStockAdjustments(
		stockAdjustmentIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<StockAdjustmentApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/stock_adjustments/close`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${stockAdjustmentIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async closeStockAdjustment(
		stockAdjustmentId: number,
		params?: Record<string, any>
	): Promise<StockAdjustmentApi> {
		return parseApiData(
			restSecurityApi.post(
				`${this.getOrganizationId()}/stock_adjustments/${stockAdjustmentId}/close`,
				undefined,
				{ params }
			)
		);
	}

	static closeCancelStockAdjustments(
		stockAdjustmentIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<StockAdjustmentApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/stock_adjustments/close_cancel`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${stockAdjustmentIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async closeCancelStockAdjustment(
		stockAdjustmentId: number,
		params?: Record<string, any>
	): Promise<StockAdjustmentApi> {
		return parseApiData(
			restSecurityApi.post(
				`${this.getOrganizationId()}/stock_adjustments/${stockAdjustmentId}/close_cancel`,
				undefined,
				{ params }
			)
		);
	}

	static restoreStockAdjustments(
		stockAdjustmentIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<StockAdjustmentApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/stock_adjustments/restore`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${stockAdjustmentIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async restoreStockAdjustment(
		stockAdjustmentId: number,
		params?: Record<string, any>
	): Promise<StockAdjustmentApi> {
		return parseApiData(
			restSecurityApi.post(
				`${this.getOrganizationId()}/stock_adjustments/${stockAdjustmentId}/restore`,
				undefined,
				{ params }
			)
		);
	}

	static removeStockAdjustments(
		stockAdjustmentIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<StockAdjustmentApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/stock_adjustments`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${stockAdjustmentIds}&${urlParams}`;

		return parseApiData(restSecurityApi.delete(endpoint));
	}

	static async removeStockAdjustment(stockAdjustmentId: number, params?: Record<string, any>): Promise<{}> {
		return parseApiData(
			restSecurityApi.delete(`${this.getOrganizationId()}/stock_adjustments/${stockAdjustmentId}`, { params })
		);
	}

	static async getStockAdjustmentsCount(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<number> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/stock_adjustments/count`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	// TAX

	static async getTaxSearchSelect(
		t: string,
		obj: Record<string, any>,
		options?: Record<string, any>
	): Promise<SearchSelectApi[]> {
		const f = mapToBase(t, obj);
		const page = getSearchSelectPage(obj);

		const res = await parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/taxes?f=${f}&page=${page}`, {
				cancelToken: options?.cancelToken,
			})
		);
		return res.map((x: ContactApi) => ({
			...x,
			label: x.name,
		}));
	}

	static async getTaxes(params?: Record<string, any>, options?: Record<string, any>): Promise<TaxApi[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/taxes`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	// CURRENCY

	static async exchangeCurrencyRate(
		currency: string,
		date: string,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<number> {
		return parseApiData(
			restSecurityApi.get(
				`${this.getOrganizationId()}/currency/exchange_rate?currency=${currency}&date=${date}`,
				{
					params,
					cancelToken: options?.cancelToken,
				}
			)
		);
	}

	// TAG

	static async getTagSearchSelect(
		t: string,
		obj: Record<string, any>,
		options?: Record<string, any>
	): Promise<SearchSelectApi[]> {
		const newObj = JSON.parse(JSON.stringify(obj));
		delete newObj.ids;

		const f = mapToBase(t, newObj);
		const page = getSearchSelectPage(newObj);
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/tags/search_select?f=${f}&page=${page}`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	// COMPANY

	static async getCompany(params?: Record<string, any>): Promise<CompanyApi> {
		return parseApiData(restSecurityApi.get(`/${this.getOrganizationId()}/company`, { params }));
	}

	static async updateCompany(data: CompanyApi, params?: Record<string, any>): Promise<CompanyApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/company`, data, { params }));
	}

	// CHANNEL

	static async getChannelSearchSelect(
		t: string,
		obj: Record<string, any>,
		options?: Record<string, any>
	): Promise<SearchSelectApi[]> {
		const newObj = JSON.parse(JSON.stringify(obj));
		delete newObj.ids;

		const f = mapToBase(t, newObj);
		const page = getSearchSelectPage(newObj);
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/integrations/channels/search_select?f=${f}&page=${page}`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	// INVOICE DETAILS

	static async getInvoiceDetails(params?: Record<string, any>): Promise<InvoiceDetailsApi> {
		return parseApiData(restSecurityApi.get(`/${this.getOrganizationId()}/invoice_details`, { params }));
	}

	static async updateInvoiceDetails(
		data: InvoiceDetailsApi,
		params?: Record<string, any>
	): Promise<InvoiceDetailsApi> {
		return parseApiData(restSecurityApi.put(`/${this.getOrganizationId()}/invoice_details`, data, { params }));
	}

	// WAREHOUSE SETTINGS

	static async getWarehouseSettings(params?: Record<string, any>): Promise<WarehouseSettingsApi> {
		return parseApiData(restSecurityApi.get(`/${this.getOrganizationId()}/settings/v2`, { params }));
	}

	static async updateWarehouseSettings(
		data: WarehouseSettingsApi,
		params?: Record<string, any>
	): Promise<WarehouseSettingsApi> {
		return parseApiData(restSecurityApi.put(`/${this.getOrganizationId()}/settings/v2`, data, { params }));
	}

	// TRANSFER

	static async getTransfers(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<TransferApi>> {
		return parseApiListDataResponse<TransferApi, TransferSummaryApi>(
			restSecurityApi.get(`/${this.getOrganizationId()}/transfer_orders`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getTransfer(transferId: number, params?: Record<string, any>): Promise<TransferApi> {
		return parseApiData(
			restSecurityApi.get(`/${this.getOrganizationId()}/transfer_orders/${transferId}`, { params })
		);
	}

	static async createTransfer(data: TransferApi, params?: Record<string, any>): Promise<TransferApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/transfer_orders`, data, { params }));
	}

	static async updateTransfer(data: TransferApi, params?: Record<string, any>): Promise<TransferApi> {
		return parseApiData(
			restSecurityApi.put(`/${this.getOrganizationId()}/transfer_orders/${data.id}`, data, { params })
		);
	}

	static async confirmTransfer(transferId: number, params?: Record<string, any>): Promise<TransferApi> {
		return parseApiData(
			restSecurityApi.post(`${this.getOrganizationId()}/transfer_orders/${transferId}/confirm`, undefined, {
				params,
			})
		);
	}

	static async closeTransfer(transferId: number, params?: Record<string, any>): Promise<TransferApi> {
		return parseApiData(
			restSecurityApi.post(`${this.getOrganizationId()}/transfer_orders/${transferId}/close`, undefined, {
				params,
			})
		);
	}

	static async closeCancelTransfer(transferId: number, params?: Record<string, any>): Promise<TransferApi> {
		return parseApiData(
			restSecurityApi.post(`${this.getOrganizationId()}/transfer_orders/${transferId}/close_cancel`, undefined, {
				params,
			})
		);
	}

	static async cancelTransfer(transferId: number, params?: Record<string, any>): Promise<TransferApi> {
		return parseApiData(
			restSecurityApi.post(`${this.getOrganizationId()}/transfer_orders/${transferId}/cancel`, undefined, {
				params,
			})
		);
	}

	static async removeTransfer(transferId: number, params?: Record<string, any>): Promise<TransferApi> {
		return parseApiData(
			restSecurityApi.delete(`${this.getOrganizationId()}/transfer_orders/${transferId}`, { params })
		);
	}

	static async restoreTransfer(transferId: number, params?: Record<string, any>): Promise<TransferApi> {
		return parseApiData(
			restSecurityApi.post(`${this.getOrganizationId()}/transfer_orders/${transferId}/restore`, undefined, {
				params,
			})
		);
	}

	static confirmMultipleTransfers(
		transferIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<TransferApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/transfer_orders/confirm`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${transferIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static cancelMultipleTransfers(
		transferIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<TransferApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/transfer_orders/cancel`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${transferIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static cancelCloseMultipleTransfers(
		transferIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<TransferApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/transfer_orders/close_cancel`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${transferIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static closeMultipleTransfers(
		transferIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<TransferApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/transfer_orders/close`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${transferIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static removeMultipleTransfers(
		transferIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<TransferApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/transfer_orders`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${transferIds}&${urlParams}`;

		return parseApiData(restSecurityApi.delete(endpoint));
	}

	static restoreMultipleTransfers(
		transferIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<TransferApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/transfer_orders/restore`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${transferIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static async getTransfersCount(params?: Record<string, any>, options?: Record<string, any>): Promise<number> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/transfer_orders/count`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	// STOCKTAKING

	static async getStocktakings(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<StocktakingApi>> {
		return parseApiListDataResponse<StocktakingApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/stocktakings`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async createStocktaking(data: StocktakingApi): Promise<StocktakingApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/stocktakings`, data));
	}

	static async updateStocktaking(data: StocktakingApi, params?: Record<string, any>): Promise<StocktakingApi> {
		return parseApiData(
			restSecurityApi.put(`/${this.getOrganizationId()}/stocktakings/${data.id}`, data, { params })
		);
	}

	static async getStocktaking(stocktakingId: number, params?: Record<string, any>): Promise<StocktakingApi> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/stocktakings/${stocktakingId}`, { params })
		);
	}

	static async startStocktaking(stocktakingId: number, params?: Record<string, any>): Promise<StocktakingApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/stocktakings/${stocktakingId}/start`, undefined, {
				params,
			})
		);
	}

	static async closeStocktaking(stocktakingId: number, params?: Record<string, any>): Promise<StocktakingApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/stocktakings/${stocktakingId}/close`, undefined, {
				params,
			})
		);
	}

	static async closeCancelStocktaking(stocktakingId: number, params?: Record<string, any>): Promise<StocktakingApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/stocktakings/${stocktakingId}/close_cancel`, undefined, {
				params,
			})
		);
	}

	static async cancelStocktaking(stocktakingId: number, params?: Record<string, any>): Promise<StocktakingApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/stocktakings/${stocktakingId}/cancel`, undefined, {
				params,
			})
		);
	}

	static async removeStocktaking(stocktakingId: number, params?: Record<string, any>): Promise<StocktakingApi> {
		return parseApiData(
			restSecurityApi.delete(`/${this.getOrganizationId()}/stocktakings/${stocktakingId}`, { params })
		);
	}

	static async restoreStocktaking(stocktakingId: number, params?: Record<string, any>): Promise<StocktakingApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/stocktakings/${stocktakingId}/restore`, undefined, {
				params,
			})
		);
	}

	static async getStocktakingSummary(
		stocktakingId: number,
		params?: Record<string, any>
	): Promise<StocktakingSummaryApi> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/stocktakings/${stocktakingId}/summary`, { params })
		);
	}

	static async getStocktakingStates(
		stocktakingId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<StocktakingProductStateSummaryApi> {
		const res = await restSecurityApi.get(`${this.getOrganizationId()}/stocktakings/${stocktakingId}/states`, {
			params,
			cancelToken: options?.cancelToken,
		});
		return res.data;
	}

	static async updateStocktakingStates(
		stocktakingId: number,
		params?: Record<string, any>
	): Promise<StocktakingProductStateApi[]> {
		return parseApiData(
			restSecurityApi.post(
				`/${this.getOrganizationId()}/stocktakings/${stocktakingId}/update_states`,
				undefined,
				{ params }
			)
		);
	}

	static async getStocktakingDrafts(
		stocktakingId: number,
		params?: Record<string, any>
	): Promise<StocktakingDraftApi[]> {
		return parseApiData(
			restSecurityApi.get(`/${this.getOrganizationId()}/stocktakings/${stocktakingId}/drafts`, { params })
		);
	}

	static async getStocktakingDraftStates(
		stocktakingId: number,
		draftId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<StocktakingDraftStateApi>> {
		return parseApiListDataResponse<StocktakingDraftStateApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/stocktakings/${stocktakingId}/drafts/${draftId}/states`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getStocktakingDraftSummary(
		stocktakingId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<StocktakingDraftSummaryStateApi[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/stocktakings/${stocktakingId}/drafts_summary`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async updateStocktakingDraft(
		stocktakingId: number,
		draftId: number,
		data: StocktakingDraftApi,
		params?: Record<string, any>
	): Promise<StocktakingDraftApi> {
		return parseApiData(
			restSecurityApi.put(`/${this.getOrganizationId()}/stocktakings/${stocktakingId}/drafts/${draftId}`, data, {
				params,
			})
		);
	}

	static async updateStocktakingDraftState(
		stocktakingId: number,
		draftId: number,
		data: StocktakingDraftStateApi,
		params?: Record<string, any>
	): Promise<StocktakingDraftStateApi> {
		return parseApiData(
			restSecurityApi.put(
				`/${this.getOrganizationId()}/stocktakings/${stocktakingId}/drafts/${draftId}/states/${data.id}`,
				data,
				{ params }
			)
		);
	}

	static async createStocktakingDraftState(
		stocktakingId: number,
		draftId: number,
		data: StocktakingDraftStateApi
	): Promise<StocktakingDraftStateApi> {
		return parseApiData(
			restSecurityApi.post(
				`/${this.getOrganizationId()}/stocktakings/${stocktakingId}/drafts/${draftId}/states`,
				data
			)
		);
	}

	static async createStocktakingDraft(
		stocktakingId: number,
		data: StocktakingDraftApi
	): Promise<StocktakingDraftApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/stocktakings/${stocktakingId}/drafts`, data)
		);
	}

	static async removeStocktakingDraft(stocktakingId: number, draftId: number): Promise<{}> {
		return parseApiData(
			restSecurityApi.delete(`/${this.getOrganizationId()}/stocktakings/${stocktakingId}/drafts/${draftId}`)
		);
	}

	static async autofillStocktakingDraft(
		stocktakingId: number,
		draftId: number,
		data: AutofillStocktakingDraftApi,
		params?: Record<string, any>
	): Promise<StocktakingDraftApi> {
		return parseApiData(
			restSecurityApi.post(
				`/${this.getOrganizationId()}/stocktakings/${stocktakingId}/drafts/${draftId}/autofill`,
				data,
				{ params }
			)
		);
	}

	static async removeStocktakingDraftState(stocktakingId: number, draftId: number, stateId: number): Promise<{}> {
		return parseApiData(
			restSecurityApi.delete(
				`/${this.getOrganizationId()}/stocktakings/${stocktakingId}/drafts/${draftId}/states/${stateId}`
			)
		);
	}

	// STOCK STATE

	static async getStockStates(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<StockStateApi>> {
		return parseApiListDataResponse<StockStateApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/stocks/states`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	// INTEGRATIONS

	static async createAndCloseIntegrationLink(
		id: number,
		data: CreateAndCloseLinkedApi,
		params?: Record<string, any>
	): Promise<LinkedApi[]> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/app_links/${id}/create_and_close`, data, {
				params,
			})
		);
	}

	static async skipIntegrationLink(id: number, params?: Record<string, any>): Promise<LinkedApi[]> {
		return parseApiData(
			restSecurityApi.put(`/${this.getOrganizationId()}/app_links/${id}/skip`, undefined, { params })
		);
	}

	static async linkIntegrationLink(
		id: number,
		data: LinkEntityFormProps,
		params?: Record<string, any>
	): Promise<LinkedApi[]> {
		return parseApiData(restSecurityApi.put(`/${this.getOrganizationId()}/app_links/${id}/link`, data, { params }));
	}

	static async correctIntegrationLinkRecipe(id: number): Promise<{}> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/integrations/linkeds/${id}/correct_link`)
		);
	}

	// TRANSACTION

	static async getTransaction(params: Record<string, any>, options?: Record<string, any>): Promise<TransactionApi[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/stock_transactions`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getFIFOTransaction(
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<TransactionApi[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/stock_transactions/fifos`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	// REPORTS

	static async getOrganizationStateReports(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		return restSecurityApi.get(`${this.getOrganizationId()}/reports/states`, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	static async getOrganizationProductReports(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		return restSecurityApi.get(`${this.getOrganizationId()}/reports/products/fifo`, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	static async getOrganizationProductSalesReports(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		return restSecurityApi.get(`${this.getOrganizationId()}/reports/sales_orders_products`, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	static async getOrganizationPurchaseReports(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		return restSecurityApi.get(`${this.getOrganizationId()}/reports/purchase_orders`, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	static async getOrganizationSalesReports(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		return restSecurityApi.get(`${this.getOrganizationId()}/reports/sales_orders`, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	static async getOrganizationTransferReports(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		return restSecurityApi.get(`${this.getOrganizationId()}/reports/transfer_orders`, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	static async getOrganizationFIFOReports(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		return restSecurityApi.get(`${this.getOrganizationId()}/reports/fifo`, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	static async getOrganizationStocktakingsReports(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		return restSecurityApi.get(`${this.getOrganizationId()}/reports/stocktakings/fifo`, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	static async getOrganizationInactiveProductReports(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		return restSecurityApi.get(`${this.getOrganizationId()}/reports/inactive_products`, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	static async getOrganizationPriceReports(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		return restSecurityApi.get(`${this.getOrganizationId()}/reports/prices`, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	static async getOrganizationCorrectionReports(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		return restSecurityApi.get(`${this.getOrganizationId()}/reports/corrections`, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	static async getOrganizationAdjustmentReports(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		return restSecurityApi.get(`${this.getOrganizationId()}/reports/stock_adjustments`, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	// CUSTOM FIELDS

	static async getCustomFieldsSearchSelect(
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<CustomFieldSearchSelectApi[]> {
		const f = mapToBase(search, params);
		const page = getSearchSelectPage(params);
		const res = await parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/custom_fields/search_select?f=${f}&page=${page}`, {
				cancelToken: options?.cancelToken,
			})
		);

		return res.map((x: CustomFieldApi) => ({
			...x,
			opts: [...x.options],
		}));
	}

	static getCustomFields(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<CustomFieldTemplateApi[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/custom_fields`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	//APPS

	static async installApplicationPost(
		provider: string,
		token: string,
		data?: ApplicationApi
	): Promise<ApplicationApi> {
		return parseApiData(
			restSecurityApi.post(`${this.getOrganizationId()}/apps/${provider}/install?access_token=${token}`, data)
		);
	}

	static async installApplication(provider: string, token: string): Promise<void> {
		window.location.href = `${
			restSecurityApi.defaults.baseURL
		}${this.getOrganizationId()}/apps/${provider}/install?access_token=${token}`;
	}

	static async reinstallApplication(app: ApplicationApi, token: string): Promise<void> {
		window.location.href = `${restSecurityApi.defaults.baseURL}${this.getOrganizationId()}/apps/${
			app.id
		}/install?provider=${app.provider.toUpperCase()}&access_token=${token}`;
	}

	static async getApp(appId: number, params?: Record<string, any>): Promise<ApplicationApi> {
		return parseApiData(restSecurityApi.get(`${this.getOrganizationId()}/apps/${appId}`, { params }));
	}

	static async exportMenu(appId: number, params?: Record<string, any>): Promise<ApplicationApi> {
		return parseApiData(
			restSecurityApi.put(`${this.getOrganizationId()}/apps/${appId}/export_menu`, undefined, { params })
		);
	}

	static async updateApp(data: ApplicationUpdateApi, params?: Record<string, any>): Promise<ApplicationApi> {
		return parseApiData(restSecurityApi.put(`${this.getOrganizationId()}/apps/${data.id}`, data, { params }));
	}

	static async removeApp(data: ApplicationApi): Promise<{}> {
		return parseApiData(restSecurityApi.delete(`${this.getOrganizationId()}/apps/${data.id}`));
	}

	static async syncApp(data: ApplicationApi): Promise<{}> {
		return parseApiData(restSecurityApi.post(`${this.getOrganizationId()}/apps/${data.id}/sync`));
	}

	static async syncApps(): Promise<ApplicationApi[]> {
		return parseApiData(restSecurityApi.post(`${this.getOrganizationId()}/apps/sync`));
	}

	static async enableApp(data: ApplicationApi, params?: Record<string, any>): Promise<ApplicationApi> {
		return parseApiData(
			restSecurityApi.put(`${this.getOrganizationId()}/apps/${data.id}/enable`, undefined, { params })
		);
	}

	static async resetApp(data: ApplicationApi, params?: Record<string, any>): Promise<ApplicationApi> {
		return parseApiData(
			restSecurityApi.put(`${this.getOrganizationId()}/apps/${data.id}/reset`, undefined, { params })
		);
	}

	static async deactivateApp(data: ApplicationApi, params?: Record<string, any>): Promise<ApplicationApi> {
		return parseApiData(
			restSecurityApi.put(`${this.getOrganizationId()}/apps/${data.id}/disable`, undefined, { params })
		);
	}

	static async getAppsConfig(appId: number): Promise<Record<string, any>> {
		return parseApiData(restSecurityApi.get(`${this.getOrganizationId()}/apps/${appId}/config`));
	}

	static async getPointOfSales(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<PointOfSaleApi[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/points_of_sale`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async installApps(
		provider: string,
		data?: ApplicationUpdateApi,
		params?: Record<string, any>
	): Promise<ApplicationApi> {
		return parseApiData(
			restSecurityApi.post(`${this.getOrganizationId()}/apps/${provider}/install`, data, { params })
		);
	}

	static async getAppsLocations(t: string, obj: Record<string, any>): Promise<SearchSelectApi[]> {
		const f = mapToBase(t, obj);
		const page = getSearchSelectPage(obj);
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/apps/locations/organizations?f=${f}&page=${page}`)
		);
	}

	static async getPriceListsSearchSelect(
		search: string,
		params: Record<string, any>,
		externalOrg?: string,
		options?: Record<string, any>
	): Promise<SearchSelectApi[]> {
		const f = mapToBase(search, params);
		const page = getSearchSelectPage(params);
		return parseApiData(
			restSecurityApi.get(
				`${
					externalOrg ? externalOrg : this.getOrganizationId()
				}/menu/price_lists/search_select?f=${f}&page=${page}`,
				{
					cancelToken: options?.cancelToken,
				}
			)
		);
	}

	static async skipEntity(id: number, params?: Record<string, any>): Promise<AppLinkApi> {
		return parseApiData(
			restSecurityApi.put(`${this.getOrganizationId()}/app_links/${id}/skip`, undefined, { params })
		);
	}

	static async createByApp(id: number, params?: Record<string, any>): Promise<AppLinkApi> {
		return parseApiData(
			restSecurityApi.put(`${this.getOrganizationId()}/app_links/${id}/create_by_app`, undefined, { params })
		);
	}

	static async linkEntity(id: number, data: AppLinkApi, params?: Record<string, any>): Promise<AppLinkApi> {
		return parseApiData(restSecurityApi.put(`${this.getOrganizationId()}/app_links/${id}/link`, data, { params }));
	}

	static async getAppsSearchSelect(
		t: string,
		obj: Record<string, any>,
		options?: Record<string, any>
	): Promise<SearchSelectApi[]> {
		const f = mapToBase(t, obj);
		const page = getSearchSelectPage(obj);
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/apps/search_select?f=${f}&page=${page}`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getLinks(params?: Record<string, any>, options?: Record<string, any>): Promise<AppLinkApi[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/app_links`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static getProductLinks(
		productId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<AppLinkApi[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/products/${productId}/app_links`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getApps(params?: Record<string, any>): Promise<ApplicationApi[]> {
		return parseApiData(restSecurityApi.get(`${this.getOrganizationId()}/apps`, { params }));
	}

	static async getAppTemplates(): Promise<ApplicationTemplateApi[]> {
		return parseApiData(restSecurityApi.get(`${this.getOrganizationId()}/app_templates`));
	}

	static async getAppLogs(params?: Record<string, any>, options?: Record<string, any>): Promise<MessageEventApi[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/app_logs`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	//DIAGNOSTIC

	static async getOrganizationDiagnosticOpenAmountReports(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<Record<string, any>[]> {
		return restSecurityApi.get(`${this.getOrganizationId()}/diagnostics/open_amount/report`, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	static async getOrganizationDiagnosticOpenAmountProduct(
		productId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<DiagnosticProductApi[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/diagnostics/product/${productId}`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getOrganizationDiagnosticProductTransactions(
		productId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<DiagnosticProductTransactionResponse[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/diagnostics/product/transactions/${productId}`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async fixDiagnosticOpenAmount(options?: Record<string, any>): Promise<{}> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/diagnostics/open_amount`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getDiagnostic2Info(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<InfoDiagnostic2ReportApi> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/diagnostics/info2`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getDiagnostic2InfoReport(
		body?: InfoDiagnostic2ReportApi,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<{}> {
		return restSecurityApi.post(`${this.getOrganizationId()}/diagnostics/info2/report`, body, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	// JPK EXPORT

	static async exportJpkFile(data: JpkExportBodyApi): Promise<string> {
		const res = await restSecurityApi.post(
			`${this.getOrganizationId()}/export/${data.file_type.toLowerCase()}`,
			data,
			{ responseType: "blob" }
		);
		return res.data;
	}

	// ALERTS

	static getAlertsData(): Promise<AlertsApi> {
		return parseApiData(restSecurityApi.get(`${this.getOrganizationId()}/alerts`));
	}

	// DIAGNOSTICS

	static getDiagnosticsTypes(organizationId?: number): Promise<string[]> {
		return parseApiData(restSecurityApi.get(`${organizationId}/diagnostics/types`));
	}

	static diagnose(types: string[], organizationId?: number, options?: Record<string, any>): Promise<string> {
		return parseApiData(
			restSecurityApi.post(`${organizationId}/diagnostics_async?type=${types}`, undefined, {
				...options,
			})
		);
	}

	static getDiagnoseProgress = (
		jobUid: string,
		organizationId?: number,
		options?: Record<string, any>
	): Promise<AxiosResponse> => {
		return restSecurityApi.get(`${organizationId}/diagnostics_async/progress/${jobUid}`, {
			...options,
		});
	};

	static diagnoseFix(fixId: string, organizationId?: number): Promise<{}> {
		return parseApiData(restSecurityApi.put(`${organizationId}/diagnostics/${fixId}/fix`));
	}

	// BUNDLES

	static createBundle(data: BundleApi): Promise<BundleApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/bundles`, data));
	}

	static updateBundle(data: BundleApi): Promise<BundleApi> {
		return parseApiData(restSecurityApi.put(`/${this.getOrganizationId()}/bundles/${data.id}`, data));
	}

	static getBundle(id: number, params?: Record<string, any>): Promise<BundleApi> {
		return parseApiData(
			restSecurityApi.get(`/${this.getOrganizationId()}/bundles/${id}`, {
				params,
			})
		);
	}

	static restoreBundle(id: number, params?: Record<string, any>): Promise<BundleApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/bundles/${id}/restore`, {
				params,
			})
		);
	}

	static confirmBundle(id: number, params?: Record<string, any>): Promise<BundleApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/bundles/${id}/confirm`, {
				params,
			})
		);
	}

	static cancelBundle(id: number, params?: Record<string, any>): Promise<BundleApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/bundles/${id}/cancel`, {
				params,
			})
		);
	}

	static cancelCloseBundle(id: number, params?: Record<string, any>): Promise<BundleApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/bundles/${id}/close_cancel`, {
				params,
			})
		);
	}

	static closeBundle(id: number, params?: Record<string, any>): Promise<BundleApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/bundles/${id}/close`, {
				params,
			})
		);
	}

	static removeBundle(id: number, params?: Record<string, any>): Promise<BundleApi> {
		return parseApiData(restSecurityApi.delete(`/${this.getOrganizationId()}/bundles/${id}`, { params }));
	}

	static async getBundles(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<BundleApi>> {
		return parseApiListDataResponse<BundleApi, StockAdjustmentSummaryApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/bundles`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getBundlesCount(params?: Record<string, any>, options?: Record<string, any>): Promise<number> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/bundles/count`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static confirmBundles(
		bundleIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<BundleApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/bundles/confirm`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${bundleIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static cancelBundles(
		bundleIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<BundleApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/bundles/cancel`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${bundleIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static cancelCloseBundles(
		bundleIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<BundleApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/bundles/close_cancel`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${bundleIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static closeBundles(
		bundleIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<BundleApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/bundles/close`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${bundleIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	static removeBundles(
		bundleIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<BundleApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/bundles`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${bundleIds}&${urlParams}`;

		return parseApiData(restSecurityApi.delete(endpoint));
	}

	static restoreBundles(
		bundleIds: string,
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<BundleApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/${this.getOrganizationId()}/bundles/restore`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?ids=${bundleIds}&${urlParams}`;

		return parseApiData(restSecurityApi.post(endpoint));
	}

	// ARCHIVE

	static getArchives(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<ArchivesApi>> {
		return parseApiListDataResponse<ArchivesApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/archives`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static getArchive(
		archiveId: string,
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<ArchivesApi> {
		return restSecurityApi.get(`${this.getOrganizationId()}/archives/${archiveId}`, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	static getArchiveInfo(archiveId: number): Promise<ArchivesApi> {
		return parseApiData<ArchivesApi>(restSecurityApi.get(`${this.getOrganizationId()}/archives/${archiveId}/info`));
	}

	static removeArchive(archiveId: number): Promise<{}> {
		return restSecurityApi.delete(`/${this.getOrganizationId()}/archives/${archiveId}`);
	}

	static fixArchive(archiveId: number): Promise<ArchivesApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/archives/${archiveId}/fix`));
	}

	static updateArchive(archiveId: number): Promise<ArchivesApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/archives/${archiveId}/update`));
	}

	static closeArchive(archiveId: number): Promise<ArchivesApi> {
		return parseApiData(restSecurityApi.post(`/${this.getOrganizationId()}/archives/${archiveId}/close`));
	}

	static closeArchiveDateTo(dateTo: string, organizationId?: number): Promise<ArchivesApi> {
		return parseApiData(restSecurityApi.post(`/${organizationId}/archives/close?date_to=${dateTo}`));
	}

	static prepareArchive(dateTo: string, organizationId?: number): Promise<ArchivesApi> {
		return parseApiData(restSecurityApi.post(`/${organizationId}/prepare_archive?date_to=${dateTo}`));
	}

	// INVOICE

	static getInvoices(params?: Record<string, any>, options?: Record<string, any>): Promise<InvoiceApi[]> {
		return parseApiData(
			restSecurityApi.get(`/${this.getOrganizationId()}/invoices`, { params, cancelToken: options?.cancelToken })
		);
	}

	static getInvoicesCount(params?: Record<string, any>, options?: Record<string, any>): Promise<number> {
		return parseApiData(
			restSecurityApi.get(`/${this.getOrganizationId()}/invoices/count`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static getInvoice(invoiceId: number, params?: Record<string, any>): Promise<InvoiceApi> {
		return parseApiData(
			restSecurityApi.get(`/${this.getOrganizationId()}/invoices/${invoiceId}`, {
				params,
			})
		);
	}

	static updateInvoice(invoiceId: number, data: InvoiceApi, params?: Record<string, any>): Promise<InvoiceApi> {
		return parseApiData(
			restSecurityApi.put(`/${this.getOrganizationId()}/invoices/${invoiceId}`, data, {
				params,
			})
		);
	}

	static addInvoicePayment(
		invoiceId: number,
		data: InvoicePaymentApi,
		params?: Record<string, any>
	): Promise<InvoiceApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/invoices/${invoiceId}/payments`, data, {
				params,
			})
		);
	}

	static updateInvoicePayment(
		invoiceId: number,
		paymentId: number,
		data: InvoicePaymentApi,
		params?: Record<string, any>
	): Promise<InvoiceApi> {
		return parseApiData(
			restSecurityApi.put(`/${this.getOrganizationId()}/invoices/${invoiceId}/payments/${paymentId}`, data, {
				params,
			})
		);
	}

	static removeInvoicePayment(invoiceId: number, paymentId: number): Promise<InvoiceApi> {
		return parseApiData(
			restSecurityApi.delete(`/${this.getOrganizationId()}/invoices/${invoiceId}/payments/${paymentId}`)
		);
	}

	static closeInvoice(invoiceId: number, params?: Record<string, any>): Promise<InvoiceApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/invoices/${invoiceId}/close`, undefined, { params })
		);
	}

	static cancelInvoice(invoiceId: number, params?: Record<string, any>): Promise<InvoiceApi> {
		return parseApiData(
			restSecurityApi.post(`/${this.getOrganizationId()}/invoices/${invoiceId}/cancel`, undefined, { params })
		);
	}

	static removeInvoice(invoiceId: number): Promise<{}> {
		return parseApiData(restSecurityApi.delete(`/${this.getOrganizationId()}/invoices/${invoiceId}`));
	}

	static getEInvoice(invoiceId: number): Promise<AxiosResponse> {
		return restSecurityApi.get(`${this.getOrganizationId()}/invoices/${invoiceId}/efaktura`, {
			responseType: "blob",
		});
	}

	static updateInvoiceBasedOnOrders(invoiceId: number, params?: Record<string, any>): Promise<InvoiceApi> {
		return parseApiData(
			restSecurityApi.post(`${this.getOrganizationId()}/invoices/${invoiceId}/update`, undefined, {
				params,
			})
		);
	}

	static createInvoice(data: InvoiceApi, params?: Record<string, any>): Promise<InvoiceApi> {
		return parseApiData(
			restSecurityApi.post(`${this.getOrganizationId()}/invoices`, data, {
				params,
			})
		);
	}

	static getProductContacts(
		productId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ProductContactApi[]> {
		return parseApiData(
			restSecurityApi.get(`${this.getOrganizationId()}/products/${productId}/contacts`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static createProductContact(
		productId: number,
		contactId: number,
		reference_price: MoneyApi,
		params?: Record<string, any>
	): Promise<ProductContactApi> {
		return parseApiData(
			restSecurityApi.put(
				`${this.getOrganizationId()}/products/${productId}/contacts/${contactId}`,
				{ reference_price },
				{
					params,
				}
			)
		);
	}

	static updateProductContact(
		productId: number,
		contactId: number,
		reference_price: MoneyApi,
		params?: Record<string, any>
	): Promise<ProductContactApi> {
		return parseApiData(
			restSecurityApi.put(
				`${this.getOrganizationId()}/products/${productId}/contacts/${contactId}`,
				{ reference_price },
				{
					params,
				}
			)
		);
	}

	static removeProductContact(productId: number, contactId: number): Promise<void> {
		return parseApiData(
			restSecurityApi.delete(`${this.getOrganizationId()}/products/${productId}/contacts/${contactId}`)
		);
	}

	//CORRECTION

	static async correctionFull(data: CorrectionFullApi): Promise<string> {
		const res = await restSecurityApi.post(`${this.getOrganizationId()}/full_recalculate`, data, {});
		return res.data;
	}

	// PRODUCTS_GROUPS
	static async getProductsGroups(
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<ListDataRequestResponse<ProductGroupsApi>> {
		return parseApiListDataResponse<ProductGroupsApi>(
			restSecurityApi.get(`${this.getOrganizationId()}/products/groups`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}
}

export { apiOrganization };
